import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StudentLayout from "../../layouts/StudentLayout";
import { Arrow, FilledDropDown } from "../../components/Icons";

const EXPECTATIONS = [
  "Daily 1-hour live sessions",
  "Interactive learning with Q&A",
  "Recordings available if you miss a class",
  "You can change your time preference later",
];

/** "15:30" -> "03:30 PM" */
const formatTimeDisplay = (time24) => {
  if (!time24) return "";
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr || "00";
  if (Number.isNaN(h)) return time24;
  const meridiem = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, "0")}:${m} ${meridiem}`;
};

const SelectClassTime = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [preferredClassTime, setPreferredClassTime] = useState("");
  const [showTimePicker, setShowTimePicker] = useState(false);
  const pickerRef = useRef(null);
  const timeInputRef = useRef(null);

  

  useEffect(() => {
    if (!showTimePicker) return;

    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowTimePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTimePicker]);

  useEffect(() => {
    if (showTimePicker) {
      timeInputRef.current?.focus();
    }
  }, [showTimePicker]);

  const handleConfirm = () => {
    if (!preferredClassTime) return;

    navigate("/student/checkout", {
      state: {
        courseId: location.state?.courseId,
        preferredClassTime,
      },
    });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <StudentLayout>
      <section className="p-8 sm:p-12 lg:p-16 font-['Roboto'] sm:px-8 sm:py-10 lg:px-16 lg:py-12">
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center justify-center text-black"
          aria-label="Go back"
        >
          <Arrow />
        </button>

        <div className="mx-auto mt-7 max-w-[1020px] text-center">
          <h1 className="text-3xl font-medium text-black sm:text-4xl lg:text-5xl lg:leading-[56px]">
            Select Your Class Time
          </h1>
          <p className="mt-3 text-base leading-[23px] text-[#4B5563] sm:text-xl">
            Choose a daily time that works best for your schedule. All times are
            in your local timezone.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-[1278px] sm:mt-16 md:mt-24 lg:mt-32">
          <div>
            <label className="mb-4 block text-lg font-medium leading-[26px] text-black sm:text-[22px]">
              Preferred Class Time (Daily)
            </label>
            <div className="relative" ref={pickerRef}>
              <button
                type="button"
                onClick={() => setShowTimePicker((open) => !open)}
                className={`flex w-full items-center justify-between rounded-lg border bg-white px-4 py-[11px] text-left text-xl font-medium transition-colors hover:bg-gray-50 ${
                  showTimePicker ? "border-[#FF6636]" : "border-[#00000080]"
                }`}
              >
                <span
                  className={
                    preferredClassTime ? "text-[#1F2937]" : "text-[#938A96]"
                  }
                >
                  {preferredClassTime
                    ? formatTimeDisplay(preferredClassTime)
                    : "Select a time"}
                </span>
                <FilledDropDown color="#000" />
              </button>

              {showTimePicker ? (
                <div className="absolute left-0 top-full z-50 mt-3 w-full max-w-sm rounded-lg border border-gray-300 bg-white p-5 shadow-lg">
                  <label
                    htmlFor="preferredClassTime"
                    className="mb-2 block text-sm font-medium text-[#374151]"
                  >
                    Pick any daily time
                  </label>
                  <input
                    ref={timeInputRef}
                    id="preferredClassTime"
                    type="time"
                    value={preferredClassTime}
                    onChange={(event) => {
                      setPreferredClassTime(event.target.value);
                    }}
                    className="h-12 w-full rounded-lg border border-[#D1D5DB] bg-white px-4 text-base text-[#1F2937] outline-none focus:border-[#FF6636]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowTimePicker(false)}
                    disabled={!preferredClassTime}
                    className="mt-4 w-full rounded-lg bg-[#FF6636] py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Done
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-16 rounded-[18px] bg-[#F5F5F5] p-6 sm:p-8 lg:px-11 lg:pt-10 lg:pb-14">
            <h2 className="text-xl font-bold text-black sm:leading-[23px] sm:text-2xl">
              What to expect:
            </h2>
            <ul className="mt-9 list-disc space-y-5 pl-5 text-sm leading-[23px] text-black sm:text-xl">
              {EXPECTATIONS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-col-reverse items-stretch justify-end gap-4  sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={handleCancel}
              className=" bg-[#F1EEEE] sm:w-[152px] px-6 py-5 text-base font-bold leading-[18px] text-black transition-colors hover:bg-[#D1D5DB]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!preferredClassTime}
              className=" bg-[#FF6636] px-14 py-5 text-base font-bold leading-[18px] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Confirm Time & Complete Purchase
            </button>
          </div>
        </div>
      </section>
    </StudentLayout>
  );
};

export default SelectClassTime;
